import { router, useForm } from "@inertiajs/react"
import { ArrowLeftIcon, ChevronUpIcon, ChevronDownIcon, StarIcon, Trash2Icon, PlusIcon, MapPinIcon, MailIcon } from "lucide-react"
import { type ReactNode, useEffect, useState } from "react"
import { toast } from "sonner"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { FieldError } from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Textarea } from "@/components/ui/textarea"
import { AdminLayout } from "@/layouts/AdminLayout"
import type { CategoryOption, EditorItem, GuideDetail, GuideStatus, Season, SubjectOption } from "@/lib/types"

interface PropertySummary {
  id: number
  name: string
  region_id: number | null
}

interface Props {
  property: PropertySummary
  guide: GuideDetail
  items: EditorItem[]
  composed: boolean
  available_pois: SubjectOption[]
  available_events: SubjectOption[]
  categories: CategoryOption[]
  property_location: { lat: number | null; lng: number | null }
  urls: {
    guides: string
    guide: string
    guide_items: string
    quick_poi: string
    reorder: string
    welcome_letter?: string
  }
}

function swapAdjacent<T extends { id: number | null }>(items: T[], id: number, direction: "up" | "down"): T[] {
  const idx = items.findIndex((i) => i.id === id)
  if (idx === -1) return items
  if (direction === "up" && idx === 0) return items
  if (direction === "down" && idx === items.length - 1) return items
  const next = [...items]
  const swap = direction === "up" ? idx - 1 : idx + 1
  ;[next[idx], next[swap]] = [next[swap], next[idx]]
  return next
}

// ---- Item card ----

interface ItemCardProps {
  item: EditorItem
  isFirst: boolean
  isLast: boolean
  canReorder: boolean
  composed: boolean
  onMove: (id: number, direction: "up" | "down") => void
  onDeleted: () => void
}

function ItemCard({ item, isFirst, isLast, canReorder, composed, onMove, onDeleted }: ItemCardProps) {
  const [note, setNote] = useState(item.host_note ?? "")
  // C3 fix: local state for optimistic highlight toggle with rollback
  const [isHighlighted, setIsHighlighted] = useState(item.is_highlighted)

  // Keep local state in sync when props change (e.g. after Inertia re-render)
  useEffect(() => { setNote(item.host_note ?? "") }, [item.host_note])
  useEffect(() => { setIsHighlighted(item.is_highlighted) }, [item.is_highlighted])

  // ItemCard only renders editable (own) rows, which always carry a delete_url + id.
  const itemUrl = item.delete_url ?? ""
  const itemId = item.id as number

  function patchItem(attrs: Record<string, string | null | boolean | number>, opts?: { onError?: () => void }) {
    router.patch(
      itemUrl,
      { guide_item: attrs },
      { preserveState: true, preserveScroll: true, onError: opts?.onError }
    )
  }

  // C2 fix: rollback local note state on failed patch
  function handleNoteBlur() {
    if (note !== (item.host_note ?? "")) {
      patchItem({ host_note: note }, {
        onError: () => setNote(item.host_note ?? ""),
      })
    }
  }

  // C3 fix: optimistic highlight with rollback on error
  function handleHighlightChange(checked: boolean) {
    setIsHighlighted(checked)
    patchItem({ is_highlighted: checked }, {
      onError: () => setIsHighlighted(item.is_highlighted),
    })
  }

  return (
    <Card className="gap-2 py-3">
      <CardHeader className="pb-0">
        <div className="flex items-center gap-2">
          {/* M2 fix: removed GripVertical icon — no DnD implemented */}
          <div className="flex flex-1 items-center justify-between gap-2 min-w-0">
            <CardTitle className="truncate text-sm font-medium">{item.subject_name}</CardTitle>
            <div className="flex shrink-0 items-center gap-1">
              <Badge variant="outline" className="text-xs capitalize">{item.subject_type}</Badge>
              {composed ? (
                /* Composed editor: ownership axis ("Your spot") — distinct from the
                   poi public/private metadata, which would mislead here (D5). */
                <Badge variant="secondary" className="text-xs">Your spot</Badge>
              ) : (
                <>
                  {item.poi_source === 'public' && (
                    <Badge className="bg-blue-50 text-blue-700 border border-blue-200 text-xs">Regional</Badge>
                  )}
                  {item.poi_source === 'private' && (
                    <Badge variant="outline" className="border border-gray-300 bg-transparent text-gray-600 text-xs">Hotel</Badge>
                  )}
                </>
              )}
              {isHighlighted && <StarIcon className="size-3.5 fill-yellow-400 text-yellow-400" aria-label="Highlighted" />}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pb-0">
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor={`note-${item.id}`} className="text-xs text-muted-foreground">Host note</Label>
            <Textarea
              id={`note-${item.id}`}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onBlur={handleNoteBlur}
              rows={2}
              placeholder="Optional note visible to guests…"
              className="text-xs"
              data-testid={`host-note-${item.id}`}
            />
          </div>
          <div className="flex items-center gap-3">
            <div className="grid gap-1">
              <Label htmlFor={`time-${item.id}`} className="text-xs text-muted-foreground">Time</Label>
              <Select
                value={item.time_context ?? "none"}
                onValueChange={(v) => patchItem({ time_context: v === "none" ? null : v })}
              >
                <SelectTrigger id={`time-${item.id}`} className="h-7 text-xs w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Any time</SelectItem>
                  <SelectItem value="morning">Morning</SelectItem>
                  <SelectItem value="afternoon">Afternoon</SelectItem>
                  <SelectItem value="evening">Evening</SelectItem>
                  <SelectItem value="all_day">All day</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-1.5 mt-4">
              <input
                id={`highlight-${item.id}`}
                type="checkbox"
                checked={isHighlighted}
                onChange={(e) => handleHighlightChange(e.target.checked)}
                className="h-3.5 w-3.5"
              />
              <Label htmlFor={`highlight-${item.id}`} className="cursor-pointer text-xs font-normal">
                Highlight
              </Label>
            </div>
          </div>
        </div>
      </CardContent>
      <CardFooter className="justify-end gap-1 pt-0">
        {/* Reorder chevrons only in flat (non-composed) mode — composed reorder is PR3b. */}
        {canReorder && (
          <>
            <Button
              variant="ghost"
              size="icon"
              className="size-7"
              disabled={isFirst}
              onClick={() => onMove(itemId, "up")}
              aria-label="Move up"
            >
              <ChevronUpIcon className="size-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="size-7"
              disabled={isLast}
              onClick={() => onMove(itemId, "down")}
              aria-label="Move down"
            >
              <ChevronDownIcon className="size-3.5" />
            </Button>
          </>
        )}
        {/* M4 fix: AlertDialog instead of window.confirm */}
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-7 text-destructive hover:text-destructive"
              aria-label="Remove item"
            >
              <Trash2Icon className="size-3.5" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove item?</AlertDialogTitle>
              <AlertDialogDescription>
                "{item.subject_name}" will be removed from this guide. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() =>
                  router.delete(itemUrl, { onSuccess: onDeleted })
                }
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardFooter>
    </Card>
  )
}

// ---- Inherited (regional) item card — READ-ONLY ----
// Renders a curator-managed spot as a finished, published item (NOT a disabled
// editable card): plain rows, no form controls, no footer. The hotel cannot edit
// inherited content in PR3a (overrides are deferred).

const TIME_LABELS: Record<string, string> = {
  morning: "Morning", afternoon: "Afternoon", evening: "Evening", all_day: "All day",
}

function InheritedItemCard({ item }: { item: EditorItem }) {
  return (
    <Card className="gap-2 py-3 bg-muted/40">
      <CardHeader className="pb-0">
        <div className="flex items-center justify-between gap-2 min-w-0">
          <CardTitle className="truncate text-sm font-medium">{item.subject_name}</CardTitle>
          <div className="flex shrink-0 items-center gap-1">
            <Badge variant="outline" className="text-xs capitalize">{item.subject_type}</Badge>
            <Badge variant="secondary" className="text-xs">Regional guide</Badge>
            {item.is_highlighted && (
              <StarIcon className="size-3.5 fill-yellow-400 text-yellow-400" aria-label="Highlighted" />
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pb-0">
        {item.host_note && (
          <p className="text-xs text-foreground/80 whitespace-pre-line">{item.host_note}</p>
        )}
        <div className="mt-2 flex items-center gap-2">
          {item.time_context && (
            <Badge variant="outline" className="text-xs">{TIME_LABELS[item.time_context] ?? item.time_context}</Badge>
          )}
          <span className="text-xs text-muted-foreground">Included from your regional guide</span>
        </div>
      </CardContent>
    </Card>
  )
}

// ---- Add items sheet ----

interface AddItemsSheetProps {
  open: boolean
  onClose: () => void
  guideItemsUrl: string
  availablePois: SubjectOption[]
  availableEvents: SubjectOption[]
  // Typed keys "poi:ID" / "event:ID" — POIs and events share a numeric id space,
  // so a bare number Set would cross-hide (event #5 hidden by poi #5).
  existingSubjectKeys: Set<string>
}

function AddItemsSheetInner({ onClose, guideItemsUrl, availablePois, availableEvents, existingSubjectKeys }: Omit<AddItemsSheetProps, "open">) {
  // H5 fix: use form-level errors from useForm
  const { data, setData, post, processing, errors, reset, transform } = useForm({
    subject_type: "poi" as "poi" | "event",
    poi_id:       "",
    event_id:     "",
    base:         "", // base error for form-level validation
  })
  const [poiSearch, setPoiSearch] = useState("")

  transform((d) => ({
    guide_item: {
      poi_id:   d.subject_type === "poi" ? d.poi_id || undefined : undefined,
      event_id: d.subject_type === "event" ? d.event_id || undefined : undefined,
    },
  }))

  // H1 fix: clear stale selection when switching subject type
  function handleTypeChange(v: "poi" | "event") {
    setData({ subject_type: v, poi_id: "", event_id: "" })
    setPoiSearch("")
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post(guideItemsUrl, {
      onSuccess: () => { reset(); onClose() },
    })
  }

  const filteredPois   = availablePois.filter((p) => !existingSubjectKeys.has(`poi:${p.id}`))
  const filteredEvents = availableEvents.filter((e) => !existingSubjectKeys.has(`event:${e.id}`))
  const currentList    = data.subject_type === "poi" ? filteredPois : filteredEvents
  const allAdded       = currentList.length === 0

  // Search-filtered POI groups
  const searchLower = poiSearch.toLowerCase()
  const searchedPois = poiSearch
    ? filteredPois.filter((p) => p.name.toLowerCase().includes(searchLower))
    : filteredPois
  const regionalPois = searchedPois.filter((p) => p.visibility === "public")
  const hotelPois    = searchedPois.filter((p) => p.visibility === "private")

  // Determine if the form is submittable
  const hasSelection = data.subject_type === "poi" ? !!data.poi_id : !!data.event_id

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4 p-6">
      <div className="grid gap-1.5">
        <Label htmlFor="add-subject-type">Type</Label>
        <Select value={data.subject_type} onValueChange={(v) => handleTypeChange(v as "poi" | "event")}>
          <SelectTrigger id="add-subject-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="poi">POI</SelectItem>
            <SelectItem value="event">Event</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* H2 fix: empty state message when all items already added */}
      {allAdded ? (
        <p className="text-sm text-muted-foreground">
          All available {data.subject_type === "poi" ? "POIs" : "events"} are already included in this guide.
        </p>
      ) : data.subject_type === "poi" ? (
        <div className="grid gap-1.5">
          <Label htmlFor="add-poi-id">POI</Label>
          <Input
            id="add-poi-search"
            aria-label="Search POIs"
            value={poiSearch}
            onChange={(e) => setPoiSearch(e.target.value)}
            placeholder="Search POIs…"
            className="h-8 text-sm"
          />
          <Select value={data.poi_id} onValueChange={(v) => setData("poi_id", v)}>
            <SelectTrigger id="add-poi-id">
              <SelectValue placeholder="Select a POI…" />
            </SelectTrigger>
            <SelectContent>
              {regionalPois.length > 0 && (
                <SelectGroup>
                  <SelectLabel className="text-xs text-blue-700">Regional</SelectLabel>
                  {regionalPois.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                  ))}
                </SelectGroup>
              )}
              {regionalPois.length > 0 && hotelPois.length > 0 && <SelectSeparator />}
              {hotelPois.length > 0 && (
                <SelectGroup>
                  <SelectLabel className="text-xs text-gray-500">Hotel</SelectLabel>
                  {hotelPois.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                  ))}
                </SelectGroup>
              )}
              {searchedPois.length === 0 && (
                <div className="py-2 px-3 text-sm text-muted-foreground">No POIs match your search.</div>
              )}
            </SelectContent>
          </Select>
          <FieldError>{errors.poi_id}</FieldError>
        </div>
      ) : (
        <div className="grid gap-1.5">
          <Label htmlFor="add-event-id">Event</Label>
          <Select value={data.event_id} onValueChange={(v) => setData("event_id", v)}>
            <SelectTrigger id="add-event-id">
              <SelectValue placeholder="Select an event…" />
            </SelectTrigger>
            <SelectContent>
              {filteredEvents.map((e) => (
                <SelectItem key={e.id} value={String(e.id)}>{e.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldError>{errors.event_id}</FieldError>
        </div>
      )}

      <FieldError>{(errors as unknown as Record<string, string>).base}</FieldError>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={processing || !hasSelection || allAdded}>
          {processing ? "Adding…" : "Add item"}
        </Button>
      </div>
    </form>
  )
}

function AddItemsSheet(props: AddItemsSheetProps) {
  const { open, onClose } = props
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Add item to guide</SheetTitle>
        </SheetHeader>
        {/* Reset form state on each open */}
        {open && <AddItemsSheetInner key={String(open)} {...props} />}
      </SheetContent>
    </Sheet>
  )
}

// ---- Quick POI sheet ----

interface QuickPoiSheetProps {
  open: boolean
  onClose: () => void
  quickPoiUrl: string
  categories: CategoryOption[]
  propertyLocation: { lat: number | null; lng: number | null }
}

function QuickPoiSheetInner({ onClose, quickPoiUrl, categories, propertyLocation }: Omit<QuickPoiSheetProps, "open">) {
  const { data, setData, post, processing, errors, reset, transform } = useForm({
    name:         "",
    description:  "",
    lat:          propertyLocation.lat != null ? String(propertyLocation.lat) : "",
    lng:          propertyLocation.lng != null ? String(propertyLocation.lng) : "",
    category_ids: [] as number[],
  })
  const [locationOpen, setLocationOpen] = useState(false)
  const [catPopoverOpen, setCatPopoverOpen] = useState(false)

  transform((d) => ({ poi: d }))

  function toggleCategory(id: number) {
    setData("category_ids", data.category_ids.includes(id)
      ? data.category_ids.filter((c) => c !== id)
      : [...data.category_ids, id]
    )
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post(quickPoiUrl, {
      onSuccess: () => { reset(); onClose() },
    })
  }

  const selectedNames = categories
    .filter((c) => data.category_ids.includes(c.id))
    .map((c) => c.name)

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4 p-6">
      <div className="grid gap-1.5">
        <Label htmlFor="qpoi-name">Name <span className="text-destructive">*</span></Label>
        <Input
          id="qpoi-name"
          aria-label="POI name"
          value={data.name}
          onChange={(e) => setData("name", e.target.value)}
          placeholder="e.g. Rooftop Bar"
        />
        <FieldError>{errors.name}</FieldError>
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="qpoi-description">Description</Label>
        <Textarea
          id="qpoi-description"
          aria-label="POI description"
          value={data.description}
          onChange={(e) => setData("description", e.target.value)}
          rows={3}
          placeholder="Brief description for guests…"
        />
        <FieldError>{errors.description}</FieldError>
      </div>

      <div className="grid gap-1.5">
        <Label>Categories</Label>
        <Popover open={catPopoverOpen} onOpenChange={setCatPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={catPopoverOpen}
              className="w-full justify-between font-normal"
            >
              {selectedNames.length > 0 ? selectedNames.join(", ") : "Select categories…"}
              <ChevronDownIcon className="ml-2 size-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-2">
            <div className="flex flex-col gap-1 max-h-56 overflow-y-auto">
              {categories.map((cat) => (
                <label
                  key={cat.id}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted"
                >
                  <Checkbox
                    checked={data.category_ids.includes(cat.id)}
                    onCheckedChange={() => toggleCategory(cat.id)}
                  />
                  {cat.icon && <span className="text-base leading-none">{cat.icon}</span>}
                  {cat.name}
                </label>
              ))}
              {categories.length === 0 && (
                <p className="px-2 py-1 text-sm text-muted-foreground">No categories available.</p>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <div className="grid gap-1.5">
        <button
          type="button"
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground w-fit"
          onClick={() => setLocationOpen((v) => !v)}
        >
          <MapPinIcon className="size-3.5" />
          {locationOpen ? "Hide location" : "Override location"}
          <ChevronDownIcon className={`size-3.5 transition-transform ${locationOpen ? "rotate-180" : ""}`} />
        </button>
        {locationOpen && (
          <div className="grid grid-cols-2 gap-2 mt-1">
            <div className="grid gap-1">
              <Label htmlFor="qpoi-lat" className="text-xs">Latitude</Label>
              <Input
                id="qpoi-lat"
                aria-label="Latitude"
                value={data.lat}
                onChange={(e) => setData("lat", e.target.value)}
                placeholder="38.7223"
                className="h-8 text-xs"
              />
            </div>
            <div className="grid gap-1">
              <Label htmlFor="qpoi-lng" className="text-xs">Longitude</Label>
              <Input
                id="qpoi-lng"
                aria-label="Longitude"
                value={data.lng}
                onChange={(e) => setData("lng", e.target.value)}
                placeholder="-9.1393"
                className="h-8 text-xs"
              />
            </div>
          </div>
        )}
      </div>

      <FieldError>{(errors as unknown as Record<string, string>).base}</FieldError>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={processing || !data.name.trim()}>
          {processing ? "Adding…" : "Add POI"}
        </Button>
      </div>
    </form>
  )
}

function QuickPoiSheet(props: QuickPoiSheetProps) {
  const { open, onClose } = props
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="overflow-y-auto w-[420px] sm:max-w-[420px]">
        <SheetHeader>
          <SheetTitle>Add POI to Guide</SheetTitle>
        </SheetHeader>
        {open && <QuickPoiSheetInner key={String(open)} {...props} />}
      </SheetContent>
    </Sheet>
  )
}

// ---- Meta form ----

interface MetaFormProps {
  guide: GuideDetail
  guideUrl: string
}

function MetaForm({ guide, guideUrl }: MetaFormProps) {
  // H5 fix: use form-level errors from useForm
  const { data, setData, patch, processing, errors, transform } = useForm({
    title:      guide.title,
    intro_text: guide.intro_text ?? "",
    status:     guide.status,
    season:     guide.season ?? "all",
    expires_at: guide.expires_at ? guide.expires_at.slice(0, 10) : "",
  })

  transform((d) => ({
    guide: {
      title:      d.title,
      intro_text: d.intro_text,
      status:     d.status,
      season:     d.season === "all" ? null : d.season,
      expires_at: d.expires_at || null,
    },
  }))

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    // M3 fix: toast on success
    patch(guideUrl, {
      onSuccess: () => toast.success("Guide saved"),
    })
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="grid gap-1.5">
        <Label htmlFor="meta-title">Title</Label>
        <Input
          id="meta-title"
          value={data.title}
          onChange={(e) => setData("title", e.target.value)}
          required
        />
        <FieldError>{errors.title}</FieldError>
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="meta-intro">Intro text</Label>
        <Textarea
          id="meta-intro"
          value={data.intro_text}
          onChange={(e) => setData("intro_text", e.target.value)}
          rows={3}
          placeholder="Brief description shown to guests…"
        />
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="meta-status">Status</Label>
        <Select value={data.status} onValueChange={(v) => setData("status", v as GuideStatus)}>
          <SelectTrigger id="meta-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* M5 fix: season only shown for seasonal_template guides */}
      {guide.guide_type === "seasonal_template" && (
        <div className="grid gap-1.5">
          <Label htmlFor="meta-season">Season</Label>
          <Select value={data.season} onValueChange={(v) => setData("season", v as Season | "all")}>
            <SelectTrigger id="meta-season">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All seasons</SelectItem>
              <SelectItem value="spring">Spring</SelectItem>
              <SelectItem value="summer">Summer</SelectItem>
              <SelectItem value="autumn">Autumn</SelectItem>
              <SelectItem value="winter">Winter</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {guide.guide_type !== "property_default" && (
        <div className="grid gap-1.5">
          <Label htmlFor="meta-expires">Expires at</Label>
          <Input
            id="meta-expires"
            type="date"
            value={data.expires_at}
            onChange={(e) => setData("expires_at", e.target.value)}
          />
        </div>
      )}

      <Button type="submit" disabled={processing} className="self-start">
        {processing ? "Saving…" : "Save changes"}
      </Button>
    </form>
  )
}

// ---- Main page ----

export default function GuidesEditor({ property, guide, items: initialItems, composed, available_pois, available_events, categories, property_location, urls }: Props) {
  const [localItems, setLocalItems] = useState(initialItems)
  const [addOpen, setAddOpen] = useState(false)
  const [quickPoiOpen, setQuickPoiOpen] = useState(false)

  useEffect(() => {
    setLocalItems(initialItems)
  }, [initialItems])

  function handleMove(id: number, direction: "up" | "down") {
    // H4 fix: capture pre-move snapshot for rollback, not stale initialItems
    const snapshot = localItems
    const reordered = swapAdjacent(snapshot, id, direction)
    setLocalItems(reordered)
    router.patch(
      urls.reorder,
      { ids: reordered.map((i) => i.id) },
      { onError: () => setLocalItems(snapshot), preserveScroll: true }
    )
  }

  // Composed mode splits the list into the hotel's own (editable) rows and inherited
  // (read-only) regional rows. Exclusion keys are typed so a POI id never hides an
  // event with the same numeric id.
  const ownItems = localItems.filter((i) => i.editable)
  const inheritedItems = localItems.filter((i) => !i.editable)
  const existingSubjectKeys = new Set(localItems.map((i) => `${i.subject_type}:${i.subject_id}`))

  const addActions = (
    <div className="flex items-center gap-2">
      <Button size="sm" variant="outline" onClick={() => setQuickPoiOpen(true)}>
        <PlusIcon className="mr-1.5 size-4" aria-hidden />
        New POI
      </Button>
      <Button size="sm" onClick={() => setAddOpen(true)}>
        <PlusIcon className="mr-1.5 size-4" aria-hidden />
        Add item
      </Button>
    </div>
  )

  function ownCard(item: EditorItem, idx: number, arr: EditorItem[]) {
    return (
      <ItemCard
        key={item.id}
        item={item}
        isFirst={idx === 0}
        isLast={idx === arr.length - 1}
        canReorder={!composed}
        composed={composed}
        onMove={handleMove}
        onDeleted={() => setLocalItems((prev) => prev.filter((i) => i.id !== item.id))}
      />
    )
  }

  const sectionHeading = "text-sm font-medium text-muted-foreground uppercase tracking-wide"

  return (
    <div>
      <div className="mb-6 flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="size-8"
          onClick={() => router.get(urls.guides)}
          aria-label="Back to guides"
        >
          <ArrowLeftIcon className="size-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold tracking-tight" data-testid="guide-title">{guide.title}</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">{property.name}</p>
        </div>
        {urls.welcome_letter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => router.visit(urls.welcome_letter!)}
          >
            <MailIcon className="mr-1.5 size-4" aria-hidden />
            Welcome Letter
          </Button>
        )}
      </div>

      <div className="flex gap-8">
        {/* Left: meta form */}
        {/* H3 fix: key on MetaForm forces reinit from new props after save */}
        <div className="w-72 shrink-0">
          <h2 className="mb-4 text-sm font-medium text-muted-foreground uppercase tracking-wide">Settings</h2>
          <MetaForm key={guide.updated_at ?? guide.id} guide={guide} guideUrl={urls.guide} />
        </div>

        <Separator orientation="vertical" className="h-auto" />

        {/* Right: items */}
        <div className="flex-1">
          {composed ? (
            <div className="flex flex-col gap-8">
              {/* Your spots — the hotel's own, editable. Shown first. */}
              <section>
                <div className="mb-4 flex items-center justify-between">
                  <h2 className={sectionHeading}>Your spots ({ownItems.length})</h2>
                  {addActions}
                </div>
                {ownItems.length === 0 ? (
                  <div className="rounded-lg border border-dashed py-12 text-center text-muted-foreground">
                    <p className="text-sm">Add your own recommendations.</p>
                    {inheritedItems.length > 0 && (
                      <p className="mt-1 text-xs">
                        Your regional guide already gives guests {inheritedItems.length} curated{" "}
                        {inheritedItems.length === 1 ? "spot" : "spots"}.
                      </p>
                    )}
                    <Button variant="link" size="sm" className="mt-2" onClick={() => setAddOpen(true)}>
                      Add your first spot
                    </Button>
                  </div>
                ) : (
                  <div className="grid gap-3">{ownItems.map(ownCard)}</div>
                )}
              </section>

              {/* From your regional guide — inherited, read-only. Suppressed when empty
                  (an empty regional section would read as abandonment). */}
              {inheritedItems.length > 0 && (
                <section>
                  <h2 className={sectionHeading}>From your regional guide ({inheritedItems.length})</h2>
                  <p className="mt-1 mb-3 text-xs text-muted-foreground">
                    Curated for your region and already live for your guests.
                  </p>
                  <div className="grid gap-3">
                    {inheritedItems.map((item) => (
                      <InheritedItemCard key={`regional:${item.subject_type}:${item.subject_id}`} item={item} />
                    ))}
                  </div>
                </section>
              )}
            </div>
          ) : (
            <div>
              <div className="mb-4 flex items-center justify-between">
                <h2 className={sectionHeading}>Items ({localItems.length})</h2>
                {addActions}
              </div>
              {localItems.length === 0 ? (
                <div className="rounded-lg border border-dashed py-16 text-center text-muted-foreground">
                  <p className="text-sm">No items yet.</p>
                  <Button variant="link" size="sm" className="mt-1" onClick={() => setAddOpen(true)}>
                    Add the first item
                  </Button>
                </div>
              ) : (
                <div className="grid gap-3">{localItems.map(ownCard)}</div>
              )}
            </div>
          )}
        </div>
      </div>

      <AddItemsSheet
        open={addOpen}
        onClose={() => setAddOpen(false)}
        guideItemsUrl={urls.guide_items}
        availablePois={available_pois}
        availableEvents={available_events}
        existingSubjectKeys={existingSubjectKeys}
      />

      <QuickPoiSheet
        open={quickPoiOpen}
        onClose={() => setQuickPoiOpen(false)}
        quickPoiUrl={urls.quick_poi}
        categories={categories}
        propertyLocation={property_location}
      />
    </div>
  )
}

GuidesEditor.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
